<template>
  <div class="bundle-payment">
    <el-card class="payment-card">
      <div class="order-header">
        <h2>Bundle Order Details</h2>
        <div class="order-status" :class="orderInfo.status.toLowerCase()">
          {{ orderInfo.status }} PAYMENT
        </div>
      </div>

      <div class="order-content">
        <div class="bundle-info">
          <div class="bundle-preview">
            <div v-for="(content, index) in previewContents" 
              :key="content._id.$id" 
              class="preview-item"
              :style="{ transform: `rotate(${(index - 1) * 5}deg)` }"
            >
              <img 
                v-if="content.cover_url" 
                :src="content.cover_url" 
                :alt="content.title"
              />
              <div v-else class="placeholder-cover">
                <i class="el-icon-picture-outline"></i>
              </div>
            </div>
          </div>
          <div class="bundle-details">
            <h3>{{ orderInfo.bundle_name }}</h3>
            <div class="bundle-meta">
              <span class="content-count">
                <i class="el-icon-folder"></i>
                {{ orderInfo.content_count || 0 }} Contents
              </span>
            </div>
            <div class="price-info">
              <template v-if="orderInfo.original_price !== orderInfo.price">
                <span class="original-price">${{ orderInfo.original_price }}</span>
                <span class="current-price">${{ orderInfo.price }}</span>
                <span class="discount-tag">
                  {{ discountPercentage }}% OFF
                </span>
              </template>
              <template v-else>
                <span class="current-price">${{ orderInfo.price }}</span>
              </template>
            </div>
          </div>
        </div>

        <div class="order-details">
          <div class="detail-item">
            <span class="label">Order ID:</span>
            <span class="value">{{ orderInfo._id?.$id || '-' }}</span>
          </div>
          <div class="detail-item">
            <span class="label">Order Date:</span>
            <span class="value">{{ formatDate(orderInfo.order_time*1000) }}</span>
          </div>
          <div class="detail-item">
            <span class="label">Club:</span>
            <span class="value">{{ orderInfo.club?.name || '-' }}</span>
          </div>
        </div>

        <div class="payment-section" v-if="orderInfo.status === 'PENDING'">
          <h3>Payment Methods</h3>
          <div class="payment-methods">
            <el-radio-group v-model="selectedPaymentMethod">
              <el-radio label="stripe">
                <i class="el-icon-bank-card"></i>
                Credit Card (Stripe)
              </el-radio>
            </el-radio-group>
          </div>

          <div class="payment-actions">
            <el-button 
              type="primary" 
              size="large" 
              :loading="processing"
              @click="handlePayment"
            >
              Pay Now
            </el-button>
          </div>

          <div class="cancel-order">
            <el-button 
              type="text" 
              @click="handleCancel"
            >
              Cancel Order
            </el-button>
          </div>
        </div>

        <div class="payment-success" v-else-if="orderInfo.status === 'COMPLETED'">
          <i class="el-icon-success"></i>
          <h3>Payment Successful!</h3>
          <p>Your bundle purchase has been completed.</p>
          <el-button type="primary" @click="viewBundle">
            View Bundle
          </el-button>
        </div>

        <router-link 
          :to="{ path: '/home/contentorders' }" 
          class="view-orders-link"
        >
          View All Orders
        </router-link>
      </div>
    </el-card>
  </div>
</template>

<script>
import { postRequest } from '@/api/eclub'

export default {
  name: 'BundlePayment',
  data() {
    return {
      orderInfo: {},
      loading: true,
      processing: false,
      selectedPaymentMethod: 'stripe'
    }
  },
  computed: {
    previewContents() {
      return (this.orderInfo.contents || []).slice(0, 3)
    },
    discountPercentage() {
      if (!this.orderInfo.price || !this.orderInfo.original_price) return 0
      return Math.round(
        ((this.orderInfo.original_price - this.orderInfo.price) / this.orderInfo.original_price) * 100
      )
    }
  },
  methods: {
    formatDate(timestamp) {
      if (!timestamp) return '-'
      return new Date(timestamp).toLocaleDateString()
    },
    async fetchOrderInfo() {
      try {
        const response = await postRequest('getBundleOrder', {
          order_id: this.$route.query.order_id
        })
        if (response.data.code === 0) {
          this.orderInfo = response.data.data
        }
      } catch (error) {
        console.error('Error fetching order:', error)
        this.$message.error('Failed to fetch order details')
      } finally {
        this.loading = false
      }
    },
    async handlePayment() {
      this.processing = true
      try {
        const response = await postRequest('processBundlePayment', {
          order_id: this.orderInfo._id.$id,
          payment_method: this.selectedPaymentMethod
        })
        
        if (response.data.code === 0) {
          this.$message.success('Payment successful')
          await this.fetchOrderInfo()
        } else {
          this.$message.error(response.data.msg || 'Payment failed')
        }
      } catch (error) {
        console.error('Payment error:', error)
        this.$message.error('Payment failed')
      } finally {
        this.processing = false
      }
    },
    async handleCancel() {
      try {
        await this.$confirm('Are you sure you want to cancel this order?', 'Warning', {
          type: 'warning'
        })
        
        const response = await postRequest('cancelBundleOrder', {
          order_id: this.orderInfo._id.$id
        })
        
        if (response.data.code === 0) {
          this.$message.success('Order cancelled successfully')
          this.$router.push('/home/contentorders')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('Error cancelling order:', error)
          this.$message.error('Failed to cancel order')
        }
      }
    },
    viewBundle() {
      this.$router.push({
        name: 'Bundle Detail',
        query: { id: this.orderInfo.bundle_id }
      })
    }
  },
  mounted() {
    this.fetchOrderInfo()
  }
}
</script>

<style lang="scss" scoped>
.bundle-payment {
  max-width: 800px;
  margin: 40px auto;
  padding: 0 20px;

  .payment-card {
    .order-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;
      padding-bottom: 16px;
      border-bottom: 1px solid #ebeef5;

      h2 {
        margin: 0;
        color: #303133;
      }

      .order-status {
        padding: 4px 12px;
        border-radius: 4px;
        font-size: 14px;
        font-weight: 500;

        &.pending {
          background-color: #fdf6ec;
          color: #e6a23c;
        }

        &.completed {
          background-color: #f0f9eb;
          color: #67c23a;
        }

        &.cancelled {
          background-color: #fef0f0;
          color: #f56c6c;
        }
      }
    }

    .bundle-info {
      display: flex;
      gap: 24px;
      margin-bottom: 32px;

      .bundle-preview {
        position: relative;
        width: 160px;
        height: 200px;

        .preview-item {
          position: absolute;
          width: 140px;
          height: 180px;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
          transition: transform 0.3s;

          img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }

          .placeholder-cover {
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #f5f7fa;
            color: #909399;
            font-size: 24px;
          }
        }
      }

      .bundle-details {
        flex: 1;

        h3 {
          margin: 0 0 12px;
          color: #303133;
          font-size: 20px;
        }

        .bundle-meta {
          margin-bottom: 16px;
          color: #606266;

          .content-count {
            display: flex;
            align-items: center;
            gap: 4px;
            
            i {
              color: #409EFF;
            }
          }
        }

        .price-info {
          display: flex;
          align-items: center;
          gap: 8px;

          .original-price {
            text-decoration: line-through;
            color: #909399;
            font-size: 16px;
          }

          .current-price {
            font-size: 24px;
            font-weight: bold;
            color: #303133;
          }

          .discount-tag {
            background: #f56c6c;
            color: white;
            padding: 2px 8px;
            border-radius: 4px;
            font-size: 14px;
          }
        }
      }
    }

    .order-details {
      margin-bottom: 32px;
      padding: 16px;
      background: #f8f9fa;
      border-radius: 4px;

      .detail-item {
        display: flex;
        margin-bottom: 8px;
        font-size: 14px;

        &:last-child {
          margin-bottom: 0;
        }

        .label {
          width: 100px;
          color: #909399;
        }

        .value {
          flex: 1;
          color: #606266;
        }
      }
    }

    .payment-section {
      h3 {
        margin-bottom: 16px;
        color: #303133;
      }

      .payment-methods {
        margin-bottom: 24px;
      }

      .payment-actions {
        text-align: center;
        margin-bottom: 16px;

        .el-button {
          min-width: 200px;
        }
      }
    }

    .payment-success {
      text-align: center;
      padding: 32px 0;

      i {
        font-size: 48px;
        color: #67c23a;
        margin-bottom: 16px;
      }

      h3 {
        margin: 0 0 8px;
        color: #303133;
      }

      p {
        color: #606266;
        margin-bottom: 24px;
      }
    }

    .view-orders-link {
      display: block;
      text-align: right;
      color: #409EFF;
      text-decoration: none;
      margin-top: 16px;
      font-size: 14px;
      
      &:hover {
        text-decoration: underline;
      }
    }
  }
}

.cancel-order {
  text-align: center;
  margin-top: 16px;

  .el-button {
    color: #909399;
    font-weight: normal;
  }
}
</style> 